// ============================================================
// TRAIT RULES - the traits that were waiting for a map.
//
// Three traits in `traitCatalog.ts` describe themselves as "Movement
// reminder only": Flyer, Hoverer and Burrower. They say that because
// when they were written there was nothing for them to act on. There is
// a map now, and everything they need already exists - `ignoresTerrain`
// on the token, `Size` on the sheet, `maxStep` in the flood fill. This
// module is the join.
//
// IT SETS DEFAULTS, IT DOES NOT TAKE THE SWITCH AWAY. The GM's manual
// toggle still wins, because a table will always have a ruling the
// catalogue has never heard of.
//
// Pure. Zero React dependency.
// ============================================================
import { SIZE_HEIGHTS } from '../constants';

/** What a combatant's own nature says about crossing ground. */
export interface TerrainRules {
  /** Walls, going and zones all stop mattering. Flyer, Phase, Incorporeal. */
  ignoresTerrain: boolean;
  /**
   * The COST of ground stops mattering, but walls do not. A Hoverer
   * crosses a mire at full speed and is still stopped by a barricade.
   */
  ignoresGoing: boolean;
  /** How far up one step may go, in metres. From Size. */
  maxStep: number;
  /** May travel through earth - everything below the ground plane. */
  canBurrow: boolean;
}

/**
 * How high a creature of this size can step without it being a climb.
 *
 * A third of its own height, which lands almost exactly where a table
 * would rule by eye: a guardsman steps onto a 0.6 m crate, a Space
 * Marine onto a 0.8 m wall, a Dreadnought over a 1.2 m barricade, and a
 * greater daemon simply walks over the lot. Nothing here is a new number
 * - it all comes off `SIZE_HEIGHTS`, which cover already uses.
 */
export const STEP_FRACTION = 1 / 3;

export function maxStepForSize(size: string | undefined): number {
  return (SIZE_HEIGHTS[size ?? 'Average'] ?? SIZE_HEIGHTS.Average) * STEP_FRACTION;
}

/** Traits that mean "the ground is not my problem". */
const FLIES = ['flyer', 'phase', 'incorporeal'];

/**
 * Read a combatant's traits for what they say about terrain.
 *
 * Takes the trait id list and the Size, both of which the roster's own
 * projection already has, so nothing new is stored anywhere.
 */
export function terrainRulesFor(traitIds: string[] = [], size?: string): TerrainRules {
  const has = (id: string) => traitIds.includes(id);
  return {
    ignoresTerrain: FLIES.some(has),
    // A Hoverer is low: it skims the mud and still meets the wall.
    ignoresGoing: has('hoverer'),
    maxStep: maxStepForSize(size),
    canBurrow: has('burrower'),
  };
}

/** The trait ids on a combatant, however the sheet spells them. */
export function traitIdsOf(defender: { traits?: { traitId?: string }[] } | null | undefined): string[] {
  return (defender?.traits ?? []).map((t) => t.traitId).filter((id): id is string => Boolean(id));
}
